import React, { useEffect, useMemo, useState } from "react";
import {
  deleteAdminProject,
  getAdminProjects,
  removeAdminProjectImage,
  saveAdminProjectAllLanguages,
  uploadAdminProjectImage,
} from "../../../api/projectsAdmin";
import { useNotification } from "../../../context/NotificationContext";
import { resolveMediaUrl } from "../../../utils/media";
import AdminEditorModal from "./AdminEditorModal";
import AdminCollapsibleSection from "./AdminCollapsibleSection";
import AdminProductListCard from "./AdminProductListCard";
import { DashboardReloadButton } from "./dashboardUi";
import { btnAccent, btnPrimary, btnSecondary, langTabActive, langTabIdle } from "./adminProductUi";

const LANGS = ["en", "ar", "ku"];
const LANG_LABELS = { en: "English", ar: "Arabic", ku: "Kurdish" };

const normLang = (value) =>
  String(value || "en")
    .toLowerCase()
    .split("-")[0];

const normKey = (value) => String(value || "").trim().toLowerCase();

/** All translatable content for a single language variant of a project. */
const emptyLangFields = () => ({
  title: "",
  description: "",
  highlight: "",
  location: "",
  sector: "",
});

const emptyTranslations = () => ({
  en: emptyLangFields(),
  ar: emptyLangFields(),
  ku: emptyLangFields(),
});

const EMPTY_FORM = {
  projectKey: "",
  coverImageUrl: "",
  isActive: true,
  images: [],
  translations: emptyTranslations(),
  ids: {},
  primaryId: null,
};

const mapRowToLangFields = (row) => ({
  title: row?.title || "",
  description: row?.description || "",
  highlight: row?.highlight || "",
  location: row?.location || "",
  sector: row?.sector || "",
});

/** Build one form (all languages) from the sibling rows of one project key. */
const mapProjectsToForm = (rows) => {
  const list = Array.isArray(rows) ? rows.filter(Boolean) : [];
  const primary = list.find((r) => normLang(r?.language) === "en") || list[0] || {};
  const translations = emptyTranslations();
  const ids = {};
  list.forEach((row) => {
    const lang = normLang(row?.language);
    if (!LANGS.includes(lang)) return;
    translations[lang] = mapRowToLangFields(row);
    ids[lang] = row?.id ?? null;
  });
  const imageSource = Array.isArray(primary?.images) && primary.images.length
    ? primary.images
    : list.find((r) => Array.isArray(r?.images) && r.images.length)?.images || [];
  return {
    projectKey: primary?.projectKey || "",
    coverImageUrl:
      primary?.coverImageUrl || list.find((r) => r?.coverImageUrl)?.coverImageUrl || "",
    isActive: typeof primary?.isActive === "boolean" ? primary.isActive : true,
    images: imageSource.map((img) => ({ id: img?.id, imageUrl: img?.imageUrl || "" })),
    translations,
    ids,
    primaryId: primary?.id ?? list[0]?.id ?? null,
  };
};

const langHasContent = (f) => Boolean((f?.title || "").trim());

const toLanguageContent = (lang, fields) => ({
  language: lang,
  title: (fields.title || "").trim(),
  description: (fields.description || "").trim(),
  highlight: (fields.highlight || "").trim(),
  location: (fields.location || "").trim(),
  sector: (fields.sector || "").trim(),
});

/** Build the multi-language save payload. Incomplete languages are omitted. */
const buildSavePayload = (form) => ({
  projectKey: (form.projectKey || "").trim(),
  coverImageUrl: (form.coverImageUrl || "").trim() || null,
  isActive: Boolean(form.isActive),
  images: (form.images || [])
    .map((img) => ({ imageUrl: String(img?.imageUrl || "").trim() }))
    .filter((img) => img.imageUrl),
  languages: LANGS.filter((lang) => langHasContent(form.translations?.[lang])).map((lang) =>
    toLanguageContent(lang, form.translations[lang])
  ),
});

const ProjectsManager = () => {
  const { notify } = useNotification();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [createForm, setCreateForm] = useState(EMPTY_FORM);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [editingKey, setEditingKey] = useState(null);
  const [activeCreateLang, setActiveCreateLang] = useState("en");
  const [activeEditLang, setActiveEditLang] = useState("en");
  const [createUploadFiles, setCreateUploadFiles] = useState([]);
  const [editUploadFiles, setEditUploadFiles] = useState([]);

  const getNextProjectKey = (list) => {
    const maxKey = (Array.isArray(list) ? list : [])
      .map((item) => Number.parseInt(String(item?.projectKey || "").trim(), 10))
      .filter((value) => Number.isFinite(value))
      .reduce((max, value) => Math.max(max, value), 0);
    return String(maxKey + 1);
  };

  const loadItems = async () => {
    setLoading(true);
    try {
      const data = await getAdminProjects();
      const normalized = Array.isArray(data) ? data : [];
      setItems(normalized);
      setCreateForm((prev) =>
        prev.projectKey.trim() ? prev : { ...prev, projectKey: getNextProjectKey(normalized) }
      );
    } catch (error) {
      notify({ type: "error", title: "Failed to load projects", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  // One card per project key; each card bundles all language variants.
  const groupedItems = useMemo(() => {
    const groups = new Map();
    items.forEach((item) => {
      const key = normKey(item?.projectKey) || `#${item?.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(item);
    });
    return [...groups.values()]
      .map((rows) => {
        const rep = rows.find((r) => normLang(r?.language) === "en") || rows[0];
        return {
          key: normKey(rep?.projectKey) || `#${rep?.id}`,
          rep,
          rows,
          languages: LANGS.filter((lang) => rows.some((r) => normLang(r?.language) === lang)),
        };
      })
      .sort((a, b) => Number(a?.rep?.id || 0) - Number(b?.rep?.id || 0));
  }, [items]);

  const refreshProjectKey = async (projectKey) => {
    await loadItems();
    if (projectKey) {
      const fresh = (await getAdminProjects().catch(() => [])).filter(
        (b) => normKey(b?.projectKey) === normKey(projectKey)
      );
      setEditForm(mapProjectsToForm(fresh));
    }
  };

  const handleCreate = async () => {
    const anyLang = LANGS.some((lang) => langHasContent(createForm.translations[lang]));
    if (!createForm.projectKey.trim() || !anyLang) {
      notify({ type: "error", title: "Project key and at least one language with a Title are required" });
      return;
    }
    setCreating(true);
    try {
      const payload = buildSavePayload(createForm);
      const savedRows = await saveAdminProjectAllLanguages(payload);
      const rep =
        (Array.isArray(savedRows) && (savedRows.find((b) => normLang(b?.language) === "en") || savedRows[0])) || null;
      if (rep?.id && createUploadFiles.length) {
        for (const file of createUploadFiles) {
          // eslint-disable-next-line no-await-in-loop
          await uploadAdminProjectImage({ id: rep.id, file });
        }
      }
      notify({ type: "success", title: "Project saved for all languages" });
      setCreateForm({ ...EMPTY_FORM, projectKey: getNextProjectKey([...items, ...(savedRows || [])]) });
      setCreateUploadFiles([]);
      setActiveCreateLang("en");
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Save failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (group) => {
    const key = normKey(group?.rep?.projectKey) || group.key;
    setEditingKey(key);
    setActiveEditLang("en");
    setEditUploadFiles([]);
    setEditForm(mapProjectsToForm(group.rows));
  };

  const saveEdit = async () => {
    if (!editingKey) return;
    const anyLang = LANGS.some((lang) => langHasContent(editForm.translations[lang]));
    if (!editForm.projectKey.trim() || !anyLang) {
      notify({ type: "error", title: "Project key and at least one language with a Title are required" });
      return;
    }
    setSaving(true);
    try {
      const payload = buildSavePayload(editForm);
      const savedRows = await saveAdminProjectAllLanguages(payload);
      const rep =
        (Array.isArray(savedRows) && (savedRows.find((b) => normLang(b?.language) === "en") || savedRows[0])) || null;
      if (rep?.id && editUploadFiles.length) {
        for (const file of editUploadFiles) {
          // eslint-disable-next-line no-await-in-loop
          await uploadAdminProjectImage({ id: rep.id, file });
        }
      }
      notify({ type: "success", title: "Project updated for all languages" });
      setEditingKey(null);
      setEditForm(EMPTY_FORM);
      setEditUploadFiles([]);
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Update failed", message: error?.response?.data?.message || error?.message || "" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (group) => {
    if (!window.confirm(`Delete project "${group?.rep?.title || group?.rep?.projectKey}" and all its language versions?`)) return;
    try {
      for (const row of group.rows) {
        // eslint-disable-next-line no-await-in-loop
        await deleteAdminProject({ id: row.id });
      }
      notify({ type: "success", title: "Project deleted" });
      await loadItems();
    } catch (error) {
      notify({ type: "error", title: "Delete failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const closeEdit = () => {
    setEditingKey(null);
    setEditForm(EMPTY_FORM);
    setEditUploadFiles([]);
    setActiveEditLang("en");
  };

  const editRepId = editForm.ids?.en ?? editForm.primaryId ?? null;

  const removeGalleryImage = async (imageId) => {
    if (!editRepId || !imageId) return;
    try {
      await removeAdminProjectImage({ id: editRepId, imageId });
      notify({ type: "success", title: "Image removed" });
      await refreshProjectKey(editForm.projectKey);
    } catch (error) {
      notify({ type: "error", title: "Remove failed", message: error?.response?.data?.message || error?.message || "" });
    }
  };

  const renderSharedFields = (form, setForm, { isEdit = false } = {}) => (
    <div className="grid sm:grid-cols-2 gap-3">
      <label className="text-sm text-slate-700">
        Project Key (shared across languages)
        <input
          type="text"
          value={form.projectKey}
          readOnly={isEdit}
          onChange={(e) => setForm((prev) => ({ ...prev, projectKey: e.target.value }))}
          className={`mt-1 w-full rounded-lg border px-3 py-2 text-sm ${
            isEdit ? "border-slate-200 bg-slate-100 text-slate-500" : "border-slate-300 bg-white"
          }`}
        />
        {isEdit ? <span className="mt-1 block text-xs text-slate-400">Shared and cannot be changed here.</span> : null}
      </label>
      <label className="inline-flex items-center gap-2 text-sm text-slate-700">
        <input
          type="checkbox"
          checked={Boolean(form.isActive)}
          onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
        />
        Active
      </label>
      <label className="text-sm text-slate-700 sm:col-span-2">
        Cover image URL (shared)
        <input
          type="text"
          value={form.coverImageUrl}
          onChange={(e) => setForm((prev) => ({ ...prev, coverImageUrl: e.target.value }))}
          placeholder="Pick from gallery below or paste a URL"
          className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
        />
      </label>
    </div>
  );

  const renderGallery = (form, setForm, { isEdit = false, uploadFiles, setUploadFiles } = {}) => (
    <div className="rounded-xl border border-slate-200 p-3">
      <p className="text-sm font-semibold text-[#011337] mb-2">Gallery images (shared across languages)</p>
      {form.images?.length ? (
        <div className="grid sm:grid-cols-3 gap-3">
          {form.images.map((img, idx) => {
            const imageUrl = resolveMediaUrl(img?.imageUrl);
            const isCover = String(form.coverImageUrl || "") === String(img?.imageUrl || "");
            return (
              <div key={`${img?.id || idx}`} className="rounded border border-slate-200 p-2">
                {imageUrl ? (
                  <img src={imageUrl} alt="" className="w-full h-28 object-cover rounded" />
                ) : (
                  <div className="w-full h-28 rounded bg-slate-100" />
                )}
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button
                    type="button"
                    onClick={() => setForm((prev) => ({ ...prev, coverImageUrl: img?.imageUrl || "" }))}
                    className={`text-xs px-2 py-1 rounded border ${
                      isCover
                        ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                        : "border-slate-300 text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    {isCover ? "Cover ✓" : "Set as cover"}
                  </button>
                  {isEdit && img?.id ? (
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(img.id)}
                      className="text-xs px-2 py-1 rounded border border-rose-200 text-rose-700 hover:bg-rose-50"
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-slate-500">No gallery images yet.</p>
      )}

      <label className="mt-3 block text-sm text-slate-700">
        Upload gallery image(s)
        <input
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => setUploadFiles(Array.from(e.target.files || []))}
          className="mt-1 block w-full text-sm"
        />
        {uploadFiles?.length ? (
          <span className="mt-1 block text-xs text-slate-500">
            {uploadFiles.length} file(s) selected — uploads when you save.
          </span>
        ) : null}
      </label>
    </div>
  );

  const renderLangContent = (form, setForm, lang) => {
    const fields = form.translations[lang] || emptyLangFields();
    const dir = lang === "ar" || lang === "ku" ? "rtl" : "ltr";

    const patch = (partial) =>
      setForm((prev) => ({
        ...prev,
        translations: { ...prev.translations, [lang]: { ...prev.translations[lang], ...partial } },
      }));

    return (
      <div className="grid sm:grid-cols-2 gap-3" dir={dir}>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Title ({lang.toUpperCase()})
          <input
            type="text"
            value={fields.title}
            onChange={(e) => patch({ title: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Description ({lang.toUpperCase()})
          <textarea
            value={fields.description}
            onChange={(e) => patch({ description: e.target.value })}
            className="mt-1 w-full min-h-[90px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700 sm:col-span-2">
          Highlight ({lang.toUpperCase()})
          <textarea
            value={fields.highlight}
            onChange={(e) => patch({ highlight: e.target.value })}
            className="mt-1 w-full min-h-[70px] rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Location ({lang.toUpperCase()})
          <input
            type="text"
            value={fields.location}
            onChange={(e) => patch({ location: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
        <label className="text-sm text-slate-700">
          Sector ({lang.toUpperCase()})
          <input
            type="text"
            value={fields.sector}
            onChange={(e) => patch({ sector: e.target.value })}
            className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white"
          />
        </label>
      </div>
    );
  };

  const renderLangTabs = (activeLang, setActiveLang, form, keyPrefix) => (
    <div className="flex flex-wrap items-center gap-2">
      {LANGS.map((lang) => {
        const hasContent = langHasContent(form.translations?.[lang]);
        return (
          <button
            key={`${keyPrefix}-${lang}`}
            type="button"
            onClick={() => setActiveLang(lang)}
            className={activeLang === lang ? langTabActive : langTabIdle}
          >
            {LANG_LABELS[lang]}
            {hasContent ? " ●" : ""}
          </button>
        );
      })}
    </div>
  );

  return (
    <section className="space-y-4">
      <div className="flex justify-end">
        <DashboardReloadButton onClick={loadItems} loading={loading} label="Reload list" />
      </div>

      <AdminCollapsibleSection
        title="Add new project"
        description="Fill in every language (English, Arabic, Kurdish). All languages are saved together."
        badge="Create"
        footer={
          <button type="button" disabled={creating} onClick={handleCreate} className={btnAccent}>
            {creating ? "Saving..." : "Save project (all languages)"}
          </button>
        }
      >
        <div className="grid gap-4">
          {renderSharedFields(createForm, setCreateForm)}
          {renderGallery(createForm, setCreateForm, {
            uploadFiles: createUploadFiles,
            setUploadFiles: setCreateUploadFiles,
          })}
          {renderLangTabs(activeCreateLang, setActiveCreateLang, createForm, "create")}
          {renderLangContent(createForm, setCreateForm, activeCreateLang)}
        </div>
      </AdminCollapsibleSection>

      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
          <h3 className="text-lg font-semibold text-[#011337]">Your projects</h3>
          {!loading ? (
            <span className="text-sm text-slate-500">
              {groupedItems.length} item{groupedItems.length !== 1 ? "s" : ""}
            </span>
          ) : null}
        </div>
        {loading ? (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-slate-200 bg-white">
            Loading projects...
          </p>
        ) : groupedItems.length ? (
          <div className="grid gap-3">
            {groupedItems.map((group) => {
              const missing = LANGS.filter((lang) => !group.languages.includes(lang));
              return (
                <AdminProductListCard
                  key={group.key}
                  id={group.rep?.id}
                  title={group.rep?.title || group.rep?.projectKey}
                  meta={[
                    group.rep?.projectKey ? `Key: ${group.rep.projectKey}` : null,
                    `Languages: ${group.languages.map((l) => l.toUpperCase()).join(", ") || "—"}`,
                    missing.length ? `Missing: ${missing.map((l) => l.toUpperCase()).join(", ")}` : null,
                  ].filter(Boolean)}
                  status={typeof group.rep?.isActive === "boolean" ? group.rep.isActive : null}
                  imageUrl={group.rep?.coverImageUrl || group.rep?.images?.[0]?.imageUrl}
                  isEditing={editingKey === group.key}
                  onEdit={() => startEdit(group)}
                  onDelete={() => handleDelete(group)}
                />
              );
            })}
          </div>
        ) : (
          <p className="text-sm text-slate-500 py-8 text-center rounded-xl border border-dashed border-slate-300 bg-slate-50">
            No projects found. Use &quot;Add new project&quot; above.
          </p>
        )}
      </div>

      <AdminEditorModal
        open={Boolean(editingKey)}
        title={`Edit project: ${editForm.translations?.en?.title || editForm.projectKey || editingKey || ""}`}
        subtitle="Edit every language, then save. Changes apply to the public projects pages."
        onClose={closeEdit}
        footer={
          <>
            <button type="button" onClick={closeEdit} className={btnSecondary}>
              Cancel
            </button>
            <button type="button" onClick={saveEdit} disabled={saving} className={btnPrimary}>
              {saving ? "Saving..." : "Save changes"}
            </button>
          </>
        }
      >
        <div className="grid gap-4">
          {renderSharedFields(editForm, setEditForm, { isEdit: true })}
          {renderGallery(editForm, setEditForm, {
            isEdit: true,
            uploadFiles: editUploadFiles,
            setUploadFiles: setEditUploadFiles,
          })}
          {renderLangTabs(activeEditLang, setActiveEditLang, editForm, "edit")}
          {renderLangContent(editForm, setEditForm, activeEditLang)}
        </div>
      </AdminEditorModal>
    </section>
  );
};

export default ProjectsManager;
